import { Component, computed, input, output, ChangeDetectionStrategy } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { formatScheduleDays, Student, School } from '@awo-care/shared-core';
import { SchoolDocumentDownloadComponent } from './school-document-download.component';
import { Timestamp } from 'firebase/firestore';

@Component({
  selector: 'lib-care-info-display',
  standalone: true,
  imports: [
    DatePipe,
    MatCardModule,
    MatDividerModule,
    MatIconModule,
    SchoolDocumentDownloadComponent,
  ],
  template: `
    <div class="care-info-container">
      @if (student()!.startInfo!.school) {
      <!-- School & Care Module Information -->
      <mat-card appearance="outlined" class="info-card">
        <mat-card-header>
          <mat-icon mat-card-avatar>menu_book</mat-icon>
          <mat-card-title i18n="@@care-info.schoolAndCare"
            >Schule & Betreuung</mat-card-title
          >
        </mat-card-header>
        <mat-card-content>
          <div class="info-list">
            @if (referenceNumber(); as referenceNumber) {
            <div class="info-item">
              <div class="info-label" i18n="@@care-info.referenceNumber">
                Antragsnummer
              </div>
              <div class="info-value">
                {{ referenceNumber }}
              </div>
            </div>
            }

            <div class="info-item">
              <div class="info-label" i18n="@@care-info.institution">
                Einrichtung
              </div>
              <div class="info-value">
                {{ getSchoolName() }}
              </div>
            </div>

            @if (getRegistrationDeadline(); as deadline) {
            <div class="info-item">
              <div class="info-label" i18n="@@care-info.registrationDeadline">
                Anmeldefrist
              </div>
              <div class="info-value">
                {{ deadline | date : 'dd.MM.yyyy' }}
              </div>
            </div>
            }

            @if (student()!.startInfo.module) {
            <div class="info-item">
              <div class="info-label" i18n="@@care-info.careProgram">
                Betreuungsprogramm
              </div>
              <div class="info-value">
                {{
                  student()!.startInfo.module.description ||
                    student()!.startInfo.module.id
                }}
              </div>
            </div>

            <div class="info-item">
              <div class="info-label" i18n="@@care-info.careHours">
                Betreuungszeiten
              </div>
              <div class="info-value">
                {{
                  formatScheduleDays(
                    student()!.startInfo.module.schedule.days || ''
                  )
                }}:
                {{ student()!.startInfo.module.schedule.startTime }}
                -
                {{ student()!.startInfo.module.schedule.endTime }}
                Uhr
              </div>
            </div>

            <div class="info-item">
              <div class="info-label" i18n="@@care-info.basicAmount">
                Grundbetrag
              </div>
              <div class="info-value">
                {{
                  student()!.startInfo.module.fee.amount.toFixed(2) || '0.00'
                }}
                EUR
              </div>
            </div>

            @if (
              student()!.startInfo.includeLunch !== undefined ||
              student()!.startInfo.lunchConfigurationId
            ) {
            <div class="info-item">
              <div class="info-label" i18n="@@care-info.lunch">Mittagessen</div>
              <div class="info-value">
                @if (lunchLabel()) {
                <span class="lunch-included">
                  {{ lunchLabel() }}
                  @if (calculateLunchAmount() > 0) {
                    (+{{ calculateLunchAmount().toFixed(2) }} EUR)
                  }
                </span>
                } @else if (student()!.startInfo.includeLunch) {
                <span class="lunch-included">
                  <span i18n="@@care-info.yes">Ja</span>
                  (+{{ calculateLunchAmount().toFixed(2) }} EUR)
                </span>
                } @else {
                <span i18n="@@care-info.no">Nein</span>
                }
              </div>
            </div>

            <div class="info-item total">
              <div class="info-label" i18n="@@care-info.totalAmount">
                Gesamtbetrag
              </div>
              <div class="info-value total-value">
                <strong>{{ calculateTotal().toFixed(2) }} EUR</strong>
              </div>
            </div>
            } }
          </div>
        </mat-card-content>
      </mat-card>
      } @if (student()!.yearInfo) {
      <!-- Year/Timing Information -->
      <mat-card appearance="outlined" class="info-card">
        <mat-card-header>
          <mat-icon mat-card-avatar>event</mat-icon>
          <mat-card-title i18n="@@care-info.timeframe">Zeitraum</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="info-list">
            <div class="info-item">
              <div class="info-label" i18n="@@care-info.schoolYear">
                Schuljahr
              </div>
              <div class="info-value">
                @if (student()!.yearInfo.year; as year) {
                {{ year }}/{{ year + 1 }}
                }
              </div>
            </div>

            @if (student()!.yearInfo.isCustomDate) {
            <div class="info-item">
              <div class="info-label" i18n="@@care-info.customStartDate">
                Individueller Starttermin
              </div>
              <div class="info-value">
                {{
                  formatDate(student()!.yearInfo.startDate)
                    | date : 'dd.MM.yyyy'
                }}
              </div>
            </div>
            } @else {
            <div class="info-item">
              <div class="info-label" i18n="@@care-info.startDate">
                Starttermin
              </div>
              <div class="info-value">01.08.{{ student()!.yearInfo.year }}</div>
            </div>
            } @if (student().contract; as contract) {
            <div class="info-item">
              <div class="info-label">
                <span i18n="@@care-info.contractEndDate">Vertragsende</span>
                <span class="hint" i18n="@@care-info.contractEndDateHint"
                  >Eine Verlängerung dieses Vertrages um ein weiteres Jahr wird Ihnen im Frühjahr für das kommende Schuljahr zugesandt.</span
                >
              </div>
              <div class="info-value">
                {{ formatDate(contract.validUntil) | date : 'dd.MM.yyyy' }}
              </div>
            </div>
            }
          </div>
        </mat-card-content>
      </mat-card>
      <!-- School Document Download Section -->
      <lib-school-document-download
        [school]="school()"
        [extraDocumentLabel]="confirmationLabel()"
        [extraDocumentDownloading]="confirmationDownloading()"
        extraDocumentTestId="confirmation-download-popover"
        (extraDocumentDownload)="confirmationDownload.emit()"
        [staticDocuments]="emergencyMedicationDocuments()"
      />
      } @if (!hasAnyInfo()) {
      <!-- No Info Available -->
      <mat-card appearance="outlined" class="info-card empty-state">
        <mat-card-content>
          <div class="empty-message">
            <mat-icon>info</mat-icon>
            <p i18n="@@care-info.noInfo">
              Keine Betreuungsinformationen verfügbar
            </p>
          </div>
        </mat-card-content>
      </mat-card>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .care-info-container {
        display: grid;
      }

      .info-card {
        margin-bottom: 2rem;
        background: #fff;
        border-radius: 8px;
        border: 1px solid rgba(0, 0, 0, 0.12);
      }

      .info-card mat-card-header {
        margin-bottom: 1rem;
      }

      .info-card mat-card-title {
        font-size: 1.1rem;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.87);
      }

      .info-list {
        display: grid;
        gap: 1rem;
      }

      .info-item {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 1rem;
      }

      .info-item.total {
        border-top: 1px solid rgba(0, 0, 0, 0.12);
        padding-top: 1rem;
        margin-top: 0.5rem;
      }

      .info-label {
        font-weight: 500;
        color: rgba(0, 0, 0, 0.87);
        flex: 1;
        min-width: 0;
      }

      .care-info-container .info-label .hint {
        display: block !important;
        font-size: 0.75rem !important;
        font-weight: 400 !important;
        color: rgba(0, 0, 0, 0.5) !important;
        font-style: italic !important;
        margin-top: 0.25rem !important;
        line-height: 1.2 !important;
      }

      .info-value {
        color: rgba(0, 0, 0, 0.7);
        text-align: right;
        flex: 1;
        min-width: 0;
      }

      .total-value {
        color: rgba(0, 0, 0, 0.87);
        font-weight: 500;
      }

      .lunch-included {
        color: var(--theme-success, #4caf50);
      }

      .empty-state {
        text-align: center;
        padding: 2rem;
        background: #fff;
        border-radius: 8px;
        border: 1px solid rgba(0, 0, 0, 0.12);
      }

      .empty-message {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1rem;
        color: rgba(0, 0, 0, 0.6);
      }

      .empty-message mat-icon {
        font-size: 2rem;
        width: 2rem;
        height: 2rem;
        opacity: 0.5;
      }

      .empty-message p {
        margin: 0;
        font-size: 0.875rem;
      }

      @media (max-width: 600px) {
        .info-item {
          flex-direction: column;
          align-items: flex-start;
          gap: 0.5rem;
        }

        .info-value {
          text-align: left;
        }
      }
    `,
  ],
})
export class CareInfoDisplayComponent {
  student = input.required<Student>();
  school = input<School | null>(null);

  /**
   * Optionale Self-Service-Bestätigung (#752): erscheint als weitere Zeile in der
   * Dokumente-Karte. `null` = nicht anzeigen (Download-Logik bleibt beim Aufrufer).
   */
  confirmationLabel = input<string | null>(null);
  confirmationDownloading = input<boolean>(false);
  confirmationDownload = output<void>();

  /** #914 — Notfallmedikament-Unterlagen als weitere Zeilen in der Dokumente-Karte. */
  protected readonly emergencyMedicationDocuments = computed(() =>
    this.student()?.healthInfo?.emergencyMedication === true
      ? [
          {
            id: 'emergency-medication-ukh',
            title: $localize`:@@student-info-form.emergency-medication-pdf-ukh:Notfallplan Anaphylaxie (Unfallkasse Hessen)`,
            url: 'shared-assets/documents/3-050_UKH_Formular_Notfallplan-Anaphylaxie-Schule.pdf',
            testId: 'emergency-medication-pdf-ukh',
          },
          {
            id: 'emergency-medication-daab',
            title: $localize`:@@student-info-form.emergency-medication-pdf-daab:Ermächtigungsbescheinigung (Deutscher Allergie- und Asthmabund)`,
            url: 'shared-assets/documents/DAAB_Ermaechtigungsbescheinigung.pdf',
            testId: 'emergency-medication-pdf-daab',
          },
        ]
      : []
  );

  /**
   * Antragsnummer — null-sicher. `registration` ist laut Typ non-null, kann aber
   * bei `no_registration`-Daten faktisch fehlen (Review F10).
   */
  referenceNumber = computed<string | null>(() => {
    const registration = this.student().registration as
      | Student['registration']
      | undefined;
    return registration?.referenceNumber ?? null;
  });

  formatScheduleDays = formatScheduleDays;

  calculateTotal(): number {
    const student = this.student();
    const startInfo = student?.startInfo;

    if (!startInfo) return 0;

    const baseAmount = startInfo.module?.fee?.amount ?? 0;
    return baseAmount + this.calculateLunchAmount();
  }

  /** Löst die Mittagessen-Kosten primär aus der neuen LunchConfiguration auf, fällt auf Legacy zurück. */
  calculateLunchAmount(): number {
    const startInfo = this.student()?.startInfo;
    if (!startInfo) return 0;

    const configId = startInfo.lunchConfigurationId;
    const school = this.school();
    if (configId && school?.lunchConfigurations?.length) {
      const config = school.lunchConfigurations.find((c) => c.id === configId);
      if (config?.fee) {
        if (config.type === 'DAILY_SELECTION') {
          const days = startInfo.selectedLunchDays ?? [];
          return config.fee.amount * days.length;
        }
        return config.fee.amount;
      }
    }
    if (startInfo.includeLunch && startInfo.lunch) {
      return startInfo.lunch.fee?.amount || 0;
    }
    return 0;
  }

  lunchLabel(): string {
    const startInfo = this.student()?.startInfo;
    if (!startInfo) return '';
    const configId = startInfo.lunchConfigurationId;
    const school = this.school();
    if (configId && school?.lunchConfigurations?.length) {
      const config = school.lunchConfigurations.find((c) => c.id === configId);
      if (config) {
        const days = startInfo.selectedLunchDays ?? [];
        return days.length > 0 ? `${config.name} (${days.join(', ')})` : config.name;
      }
    }
    return startInfo.lunch?.description ?? '';
  }

  formatDate(date: Date | Timestamp | any | undefined): Date | null {
    if (!date) return null;

    // If it's already a Date object
    if (date instanceof Date) return date;

    // If it's a Timestamp with toDate method
    if (date?.toDate && typeof date.toDate === 'function') {
      return date.toDate();
    }

    // If it's a date string or timestamp number, convert to Date
    if (typeof date === 'string' || typeof date === 'number') {
      return new Date(date);
    }

    // If we can't handle the date format, return null
    return null;
  }

  hasAnyInfo(): boolean {
    const student = this.student();
    return !!(student?.startInfo?.school || student?.yearInfo);
  }

  getRegistrationDeadline(): Date | null {
    const school = this.school();
    if (!school?.registrationDeadline) return null;
    return this.formatDate(school.registrationDeadline);
  }

  getSchoolName(): string {
    // Prefer passed school data, fallback to embedded data
    const school = this.school();
    if (school?.schoolName) {
      return school.schoolName;
    }

    // Fallback to embedded school data
    return this.student()?.startInfo?.school?.schoolName || 'Unbekannte Schule';
  }
}
